import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

/**
 * FooterRow is the slot-based row container for footer composition.
 *
 * Why this exists:
 * - Provides a consistent left/center/right layout contract for footer rows.
 * - Encapsulates responsive alignment and row spacing in one reusable place.
 *
 * Use this when:
 * - Building a footer row with any combination of left, center, and right content.
 * - You need tighter vertical spacing for secondary rows (set tight=true).
 *
 * Typical usage:
 * <FooterRow center={centerColumn} right={rightColumn} />
 */
function FooterRow({ left, center, right, tight = false }) {
	const rowPaddingY = tight
		? { xs: '0', sm: '0' }
		: { xs: '.25rem', sm: '.3rem' };
	const rowGap = tight ? { xs: '0', sm: '0' } : { xs: '.35rem', sm: '.4rem' };

	return (
		<Grid
			container
			sx={{
				alignItems: 'center',
				py: rowPaddingY,
				rowGap,
			}}
		>
			<Grid item xs={12} sm="auto">
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
					{left}
				</Box>
			</Grid>
			<Grid item xs={12} sm>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: '.5rem .75rem',
					}}
				>
					{center}
				</Box>
			</Grid>
			<Grid item xs={12} sm="auto">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: { xs: 'flex-start', sm: 'flex-end' },
					}}
				>
					{right}
				</Box>
			</Grid>
		</Grid>
	);
}

FooterRow.propTypes = {
	left: PropTypes.node,
	center: PropTypes.node,
	right: PropTypes.node,
	tight: PropTypes.bool,
};

export default FooterRow;
